import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { DialogService } from '@falcon/app/shared/services/dialog/dialog.service';
import { AuthStore } from '@falcon/app/core/storage/auth/auth.store';
import { UserRolesEnum } from '@falcon/app/shared/models/user.model';
import { COMPANY_REGISTRATIONS_PUBLIC_API_URL } from '../api/company-registration/company-registration.service';
import { ContractService } from '../api/contract/contract.service';
import { NEWCOMERS_PUBLIC_API_URL } from '../api/newcomer/newcomer-api.service';
import { SKIP_ERROR_DIALOG } from './skip-error-dialog.context';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private dialogService = inject(DialogService);
  private authStore = inject(AuthStore);
  private contractService = inject(ContractService);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (request.context.get(SKIP_ERROR_DIALOG)) {
          return throwError(() => error);
        }

        // Skip automatic generic error dialog for manual registration to invoke it manually
        if (error.url?.includes(NEWCOMERS_PUBLIC_API_URL)) {
          return throwError(() => error);
        }

        // Self-onboarding wizard: every public step renders its own inline error (e.g. the
        // "e-mail already exists" message on the contact step, BCJ-20132), so the generic dialog
        // would only duplicate it.
        if (error.url?.includes(COMPANY_REGISTRATIONS_PUBLIC_API_URL)) {
          return throwError(() => error);
        }

        if (
          error.status === HttpStatusCode.BadRequest &&
          request.url.includes(this.contractService.CONTRACTS_API_URL)
        ) {
          this.dialogService.openAdminErrorDialog(error.error);
        } else if (error.status === HttpStatusCode.Conflict) {
          this.dialogService.openAdminErrorDialog(error.error);
        } else if (
          ![HttpStatusCode.Unauthorized, HttpStatusCode.GatewayTimeout].includes(error.status)
        ) {
          this.authStore.hasRoles([
            UserRolesEnum.FULL_ADMIN,
            UserRolesEnum.SUPER_ADMIN,
            UserRolesEnum.SALES_ADMIN,
            UserRolesEnum.PAYROLL_ADMIN,
            UserRolesEnum.PAYROLL_SALES,
            UserRolesEnum.PAYROLL_MANAGEMENT,
          ])
            ? this.dialogService.openAdminErrorDialog(error.error)
            : this.dialogService.openContactSupportDialog();
        }

        return throwError(() => error);
      })
    );
  }
}
